import { defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/plugins/firebase';
export default defineComponent({
    name: 'AuthView',
    setup() {
        const router = useRouter();
        const phoneNumber = ref('');
        const verificationCode = ref('');
        const codeSent = ref(false);
        const loading = ref(false);
        const errorMessage = ref('');
        const successMessage = ref('');
        // Firebase Functions references
        const startCustomPhoneAuthFn = httpsCallable(functions, 'startCustomPhoneAuth');
        const completeCustomPhoneAuthFn = httpsCallable(functions, 'completeCustomPhoneAuth');
        const clearMessages = () => {
            errorMessage.value = '';
            successMessage.value = '';
        };
        const sendCode = async () => {
            if (!phoneNumber.value)
                return;
            clearMessages();
            loading.value = true;
            try {
                // Kald startCustomPhoneAuth funktionen
                const result = await startCustomPhoneAuthFn({ phoneNumber: phoneNumber.value });
                const data = result.data;
                if (data.success) {
                    codeSent.value = true;
                    successMessage.value = `Bekræftelseskode sendt til ${phoneNumber.value}`;
                }
                else {
                    // Håndter fejl baseret på error type
                    if (data.error === 'missing_phone_number') {
                        errorMessage.value = 'Telefonnummer mangler. Indtast venligst et gyldigt telefonnummer.';
                    }
                    else if (data.error === 'invalid_phone_number') {
                        errorMessage.value = 'Ugyldigt telefonnummer. Kontroller formatet (+45 12 34 56 78).';
                    }
                    else if (data.error === 'twilio_timeout' || data.error === 'twilio_auth_failed') {
                        errorMessage.value = 'Fejl ved forbindelse til SMS-tjenesten. Prøv igen senere.';
                    }
                    else if (data.error === 'rate_limit_exceeded' || data.error === 'max_attempts_reached') {
                        errorMessage.value = 'For mange forsøg. Prøv igen senere.';
                    }
                    else {
                        errorMessage.value = 'Fejl ved afsendelse af kode. Prøv igen.';
                    }
                }
            }
            catch (error) {
                console.error('Error sending code:', error);
                errorMessage.value = 'Kunne ikke sende SMS kode. Kontakt support hvis problemet fortsætter.';
            }
            finally {
                loading.value = false;
            }
        };
        const verifyCode = async () => {
            if (!verificationCode.value)
                return;
            clearMessages();
            loading.value = true;
            try {
                // Kald completeCustomPhoneAuth funktionen
                const result = await completeCustomPhoneAuthFn({
                    phoneNumber: phoneNumber.value,
                    phoneCode: verificationCode.value,
                });
                const data = result.data;
                if (data.success && data.token) {
                    // Login med custom token
                    await signInWithCustomToken(auth, data.token);
                    successMessage.value = 'Login succesfuld! Omdirigerer...';
                    setTimeout(() => {
                        router.push('/');
                    }, 1000);
                }
                else {
                    // Håndter fejl baseret på error type
                    if (data.error === 'missing_phone_number') {
                        errorMessage.value = 'Telefonnummer mangler. Prøv igen.';
                    }
                    else if (data.error === 'missing_phone_code') {
                        errorMessage.value = 'Bekræftelseskode mangler. Indtast koden fra SMS.';
                    }
                    else if (data.error === 'invalid_code') {
                        errorMessage.value = 'Ugyldig bekræftelseskode. Kontroller koden og prøv igen.';
                    }
                    else if (data.error === 'verification_failed') {
                        errorMessage.value = 'Verifikation fejlede. Prøv igen eller kontakt support.';
                    }
                    else {
                        errorMessage.value = 'Fejl ved bekræftelse af kode. Prøv igen.';
                    }
                }
            }
            catch (error) {
                console.error('Error verifying code:', error);
                errorMessage.value = 'Kunne ikke bekræfte koden. Kontakt support hvis problemet fortsætter.';
            }
            finally {
                loading.value = false;
            }
        };
        const resetForm = () => {
            codeSent.value = false;
            verificationCode.value = '';
            clearMessages();
        };
        return {
            phoneNumber,
            verificationCode,
            codeSent,
            loading,
            errorMessage,
            successMessage,
            sendCode,
            verifyCode,
            resetForm,
        };
    },
});
